/* eslint-disable @typescript-eslint/no-misused-promises */
import Button from "@ch/components/Buttons/Button";
import Modal from "@ch/components/Modals/Modal";
import { CustomSelect } from "@ch/components/Select/Select";
import useUpdateUser from "@ch/helpers/hooks/useUpdateUser";
import useUserDetails from "@ch/helpers/hooks/useUserDetails";
import { NotifyError, NotifySuccess } from "@ch/helpers/utils/helpers";
import useGetLanguages from "@ch/pages/MenuPage/single-pages/ProfileInformationPage/hooks/useGetLanguages.ts";
import type React from "react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

interface PreferredLanguageModalProps {
  modalId: string;
  onClose: () => void;
  onSuccess?: () => void;
}

const PreferredLanguageModal: React.FC<PreferredLanguageModalProps> = ({
  modalId,
  onClose,
  onSuccess,
}) => {
  const navigate = useNavigate();
  const { user } = useUserDetails();
  const { languageOptions } = useGetLanguages();
  const { initiateUpdateUser, loading } = useUpdateUser();

  const [selectedLanguageIds, setSelectedLanguageIds] = useState<string[]>(
    user?.preferredLanguageIds ?? [],
  );

  const handleSave = async () => {
    if (selectedLanguageIds.length === 0) {
      NotifyError("Please select at least one language.");
      return;
    }

    await initiateUpdateUser({
      requestBody: {
        preferredLanguageIds: selectedLanguageIds,
      },
      onSuccess: () => {
        NotifySuccess("Preferred language updated.");
        onSuccess?.();
        onClose();
      },
    });
  };

  const handleGoToProfile = () => {
    void navigate("/menu/profile-information");
    onClose();
  };

  return (
    <Modal
      headerTitle="Preferred Language"
      modalBoxClassName="!h-fit"
      modalId={modalId}
      onDismiss={onClose}
    >
      <div className="flex flex-col gap-6">
        <p className="text-center text-gray">
          Choose your preferred language to improve your chances of getting the
          best-matched coach.
        </p>

        <CustomSelect
          intent="outline"
          isMulti
          label="Preferred Language"
          name="preferredLanguageIds"
          noOptionsMessage={() => "No languages found"}
          onChange={(val) => {
            const newValues = (
              val as unknown as { value: string; label: string }[]
            )?.map((v) => v.value);
            setSelectedLanguageIds(newValues);
          }}
          options={languageOptions}
          placeholder="Select Language"
          value={languageOptions?.filter((option) => {
            return selectedLanguageIds.includes(option?.value);
          })}
        />

        <div className="flex flex-col gap-3">
          <Button isLoading={loading} onClick={handleSave}>
            Save and Continue
          </Button>
          <Button intent="ghost" onClick={handleGoToProfile}>
            Update on Profile Information
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default PreferredLanguageModal;
